import { createWebHistory, createRouter } from "vue-router";
import { useUserStore } from "../stores/user.store";
import { subscriptionGuard, alreadySubscribedGuard } from "../guards/subscription.guard";
import { setAuthChecking } from "../service/auth-state";
import { isChunkLoadError, reloadOnceAfterLoadError } from "../pwa";

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      component: () => import('../views/Landing.view.vue'),
    },
    {
      path: '/login',
      component: () => import('../views/Login.view.vue'),
    },
    {
      path: '/register',
      component: () => import('../views/Register.view.vue'),
    },
    {
      path: '/email-confirmed',
      component: () => import('../views/EmailConfirmed.view.vue'),
    },
    {
      path: '/forgot-password',
      component: () => import('../views/ForgotPassword.view.vue'),
    },
    {
      path: '/reset-password',
      component: () => import('../views/ResetPassword.view.vue'),
    },
    {
      // Ficha pública de un modelo: es el destino del QR impreso en el ticket,
      // se abre desde el celular sin iniciar sesión
      path: '/m/:contest_id/:model_id',
      name: 'PublicModel',
      component: () => import('../views/PublicModel.view.vue'),
      meta: { public: true },
    },
    {
      // Votación del público (premio Cuatro Ases): la abre el QR general del
      // concurso o el botón de la ficha del modelo, sin iniciar sesión
      path: '/votar/:contest_id',
      name: 'PublicVoting',
      component: () => import('../views/PublicVoting.view.vue'),
      meta: { public: true },
    },
    {
      // Resultados públicos: listado de concursos y premiados por categoría.
      // Se ven sin iniciar sesión (concursantes, familiares, visitantes)
      path: '/resultados',
      name: 'PublicContests',
      component: () => import('../views/PublicContests.view.vue'),
      meta: { public: true },
    },
    {
      path: '/resultados/:contest_id',
      name: 'PublicContestResults',
      component: () => import('../views/PublicContestResults.view.vue'),
      meta: { public: true },
    },
    {
      path: '/home',
      component: () => import('../layouts/Home.layout.vue'),
      children: [
        {
          path: '',
          component: () => import('../views/Home.view.vue'),
        },
        {
          path: 'about',
          component: () => import('../views/About.view.vue'),
        },
        {
          path: 'profile',
          component: () => import('../views/UserProfile.view.vue'),
        },
        {
          path: 'admin',
          component: () => import('../views/Admin.view.vue'),
          meta: { roles: ['ADMIN', 'JUEZ'] },
        },
        {
          path: 'admin/users',
          component: () => import('../views/AdminUsers.view.vue'),
          meta: { roles: ['ADMIN'], platform: true },
        },
        {
          path: 'admin/clubs',
          component: () => import('../views/AdminClubs.view.vue'),
          meta: { roles: ['ADMIN'], platform: true },
        },
        {
          // Miembros del club para su administrador (req. 1.5): nombra
          // administradores y jueces solo de los clubes que administra
          path: 'admin/my-clubs',
          component: () => import('../views/MyClubs.view.vue'),
          meta: { roles: ['ADMIN'] },
        },
        {
          path: 'admin/contests',
          component: () => import('../views/AdminContests.view.vue'),
          meta: { roles: ['ADMIN', 'JUEZ'] },
        },
        {
          path: 'admin/contests/create',
          component: () => import('../views/CreateContest.view.vue'),
          meta: { roles: ['ADMIN'], unsavedForm: true },
        },
        {
          path: 'admin/contests/edit/:contest_id',
          component: () => import('../views/EditContest.view.vue'),
          meta: { roles: ['ADMIN'], unsavedForm: true },
        },
        {
          path: 'model/create/:contest_id',
          component: () => import('../views/CreateModel.view.vue'),
          meta: { unsavedForm: true },
        },
        {
          path: 'model/create',
          component: () => import('../views/CreateModel.view.vue'),
          meta: { unsavedForm: true },
        },
        {
          path: 'model/edit/:model_id',
          component: () => import('../views/EditModel.view.vue'),
          meta: { unsavedForm: true },
        },
        {
          path: 'model/edit/:model_id/:contest_id',
          component: () => import('../views/EditModel.view.vue'),
          meta: { unsavedForm: true },
        },
        {
          path: 'model/my-models',
          component: () => import('../views/MyModels.view.vue'),
        },
        {
          path: 'contest/subscribe/:contest_id',
          name: 'SubscribeModels',
          component: () => import('../views/SubscribeModels.view.vue'),
          beforeEnter: subscriptionGuard,
        },
        {
          path: 'contest/subscription/:contest_id',
          component: () => import('../views/ContestSubscription.view.vue'),
          beforeEnter: alreadySubscribedGuard,
        },
        {
          // Pantalla del juez (puntuar y premiar). El chequeo por concurso
          // (club organizador) lo hace la vista con canJudgeContest
          path: 'admin/contest/categories/:contest_id',
          component: () => import('../views/ContestCategories.view.vue'),
          meta: { roles: ['ADMIN', 'JUEZ'] },
        },
        {
          path: 'admin/contest/surface/:contest_id',
          component: () => import('../views/ContestSurface.view.vue'),
          meta: { roles: ['ADMIN'] },
        },
        {
          path: 'admin/contest/voting/:contest_id',
          component: () => import('../views/ContestVoting.view.vue'),
          meta: { roles: ['ADMIN'] },
        },
        {
          path: 'admin/abm-categorias/:contest_id',
          component: () => import('../modules/Category/ABMContestCategory.view.vue'),
          meta: { roles: ['ADMIN'] },
        },
        {
          path: 'admin/abm-categorias/:contest_id/:category_id',
          component: () => import('../modules/Category/ABMContestCategory.view.vue'),
          meta: { roles: ['ADMIN'] },
        },
        {
          path: 'admin/category-templates',
          component: () => import('../views/AdminCategoryTemplates.view.vue'),
          meta: { roles: ['ADMIN'], platform: true },
        },
        {
          path: 'admin/contest-payments',
          component: () => import('../views/AdminContestPayments.view.vue'),
          meta: { roles: ['ADMIN'] },
        },
        {
          path: 'admin/reprint-cards',
          component: () => import('../views/AdminReprintCards.view.vue'),
          meta: { roles: ['ADMIN'] },
        },
        {
          path: 'admin/mp-config',
          component: () => import('../views/AdminMpConfig.view.vue'),
          meta: { roles: ['ADMIN'], platform: true },
        },
        {
          path: 'admin/diplomas',
          component: () => import('../views/AdminDiplomas.view.vue'),
          meta: { roles: ['ADMIN'] },
        },
        {
          path: 'payment/success',
          component: () => import('../views/PaymentSuccess.view.vue'),
        },
        {
          path: 'payment/failure',
          component: () => import('../views/PaymentFailure.view.vue'),
        },
        {
          path: 'payment/pending',
          component: () => import('../views/PaymentPending.view.vue'),
        },
      ],
    },
  ],
});

const PUBLIC_ROUTES = ["/login", "/", "/register", "/email-confirmed", "/forgot-password", "/reset-password"];

router.beforeEach(async (to, _from, next) => {
  const store = useUserStore();
  const isPublicRoute = PUBLIC_ROUTES.includes(to.path) || to.meta?.public === true;

  if (isPublicRoute) {
    return next();
  }

  if (!store.isLoggedIn) {
    setAuthChecking(true);
    try {
      await store.loadUserData();
    } catch {
      setAuthChecking(false);
      return next("/login");
    }
    setAuthChecking(false);

    if (!store.isLoggedIn) {
      return next("/login");
    }
  }

  // Red de seguridad: toda pantalla bajo /home/admin es de jueces o
  // administradores (globales o de club), aunque a la ruta le falte meta.roles.
  // Un participante común nunca entra, ni escribiendo la URL a mano
  if (to.path.startsWith('/home/admin') && !store.hasAdminAccess) {
    return next('/home');
  }

  const requiredRoles = (to.meta as any)?.roles as string[] | undefined;
  if (requiredRoles && requiredRoles.length > 0) {
    const role = store.user?.roleUser;
    const globalOk = !!role && requiredRoles.includes(role);
    // Los roles de club (ADMIN/JUEZ por club) habilitan las pantallas de
    // administración, salvo las marcadas como exclusivas de plataforma
    const clubOk =
      !(to.meta as any)?.platform &&
      (store.user?.memberships ?? []).some((m) => requiredRoles.includes(m.club_role));
    if (!globalOk && !clubOk) {
      return next("/home");
    }
  }

  next();
});

// Las vistas se cargan por demanda: si la pestaña quedó con una versión vieja
// de la app, el archivo de la vista ya no existe y la navegación falla con la
// pantalla en blanco (bug del QR, req. 1.4). Se recarga la página apuntando a
// la ruta pedida para traer la versión vigente, una sola vez
router.onError((error, to) => {
  if (isChunkLoadError(error)) {
    reloadOnceAfterLoadError(to.fullPath);
  }
});

export default router;
